import { Injectable } from '@angular/core';
import * as _ from 'lodash-es';
import { BehaviorSubject, combineLatest, interval, Subject } from 'rxjs';
import { distinctUntilChanged, filter, first, takeUntil } from 'rxjs/operators';
import { MessagingService } from 'src/app/core/messaging/messaging.service';
import { MapMenuCode } from 'src/app/feature/map-viewer/map-menus/map-menu-list';
import { MapService } from 'src/app/feature/map-viewer/map.service';
import { MenuService } from 'src/app/shared/common/layout/menu.service';
import { MapWorkspace } from 'src/app/shared/map-data-services/mapWorkspace/map-workspace';
import { WorkspaceType } from 'src/app/shared/map-data-services/mapWorkspace/map-workspace.types';
import { Actor } from 'src/app/shared/user/actor';
import { v4 as uuidv4 } from 'uuid';

import { TranslationService } from 'src/app/core/translation/translation.service';
import { Feature } from '../../map-data-services/feature/feature';
import { Layer } from '../../map-data-services/layer/layer';
import { MapWorkspacePermissionType } from '../../map-data-services/mapWorkspace/map-workspace-permission';
import { ProjectTaskMigrationStatus } from '../../map-data-services/project/project';
import { CheckVisibilityService } from '../components/check-visibility.service';
import { LayersStore } from '../current-layers/layers-store.service';
import { MapWorkspacesStoreService } from '../current-map-workspaces/map-workspaces-store.service';
import { ProjectStreamService } from '../current-project/project-stream.service';
import { FormStatus, Task, TaskFeature, TaskPanelTabs, TaskPatchDTO, TaskStatus } from '../task/task';
import { TaskService } from '../task/task.service';

@Injectable({
    providedIn: 'root'
})
export class TasksStore {
    mapWorkspaceTasksLoadingStream = new BehaviorSubject<string[]>([]);
    mapWorkspaceTasksLoadedStream = new BehaviorSubject<boolean>(false);
    mapWorkspaceTasksStream = new BehaviorSubject<Task[]>([]);
    mapWorkspaceActiveTasksStream = new BehaviorSubject<Task[]>([]);
    mapWorkspaceClosedTasksStream = new BehaviorSubject<Task[]>([]);
    mapWorkspaceActiveTasksByTemplateSeriesIdStream = new BehaviorSubject<{ [templateId: string]: Task[] }>(null);

    refreshMapWorkspaceTasksStreams = new BehaviorSubject<Task[]>([]);

    private savedVisibleTaskIds: string[] = [];

    constructor(
        private mapService: MapService,
        private menuService: MenuService,
        private projectStream: ProjectStreamService,
        private taskService: TaskService,
        private layersStore: LayersStore,
        private mapWorkspacesStore: MapWorkspacesStoreService,
        private translate: TranslationService,
        private checkVisibilityService: CheckVisibilityService,
        private messaging: MessagingService
    ) {
        // Initialize the service only for GIS workspace type
        combineLatest([
            mapWorkspacesStore.currentMapWorkspaceStream.pipe(
                distinctUntilChanged(),
                filter(workspace => workspace?.type === WorkspaceType.GIS)
            ),
            mapService.baseMapReadyStream.pipe(first((isMapReady: boolean) => isMapReady))
        ]).subscribe(([currentMapWorkspace, isMapReady]: [MapWorkspace, boolean]) => {
            if (
                isMapReady &&
                currentMapWorkspace &&
                !this.checkVisibilityService.restrictAccess(
                    [MapWorkspacePermissionType.FULL_ACCESS],
                    currentMapWorkspace
                ) &&
                projectStream.taskMigrationStatusStream.getValue() === ProjectTaskMigrationStatus.COMPLETED
            ) {
                this.loadCurrentWorkspaceTasks(currentMapWorkspace);
            }
        });

        combineLatest([this.mapWorkspaceActiveTasksStream, this.mapWorkspaceClosedTasksStream]).subscribe(
            ([activeTasks, closedTasks]: [Task[], Task[]]) => {
                const combinedTasks = activeTasks.concat(closedTasks);
                combinedTasks.forEach(task => {
                    task.totalTaskedForms = this.totalTaskedForms(task);
                });
                this.mapWorkspaceTasksStream.next(combinedTasks);
            }
        );

        // loading the current workspace tasks on opening task menu
        menuService.activeMenusStream.pipe().subscribe(menus => {
            const currentMapWorkspace = mapWorkspacesStore.currentMapWorkspaceStream.getValue();
            if (
                menus.includes(MapMenuCode.TASKS) &&
                currentMapWorkspace &&
                currentMapWorkspace.type === WorkspaceType.GIS &&
                !this.checkVisibilityService.restrictAccess(
                    [MapWorkspacePermissionType.FULL_ACCESS],
                    currentMapWorkspace
                )
            ) {
                this.loadCurrentWorkspaceTasks(currentMapWorkspace);
            }
        });
    }

    public async loadCurrentWorkspaceTasks(workspace: MapWorkspace): Promise<void> {
        // Only load tasks for GIS workspace type
        if (!workspace || workspace.type !== WorkspaceType.GIS) {
            return;
        }

        this.saveVisibleTasks();

        this.mapWorkspaceTasksStream.next([]);

        if (workspace && workspace.id) {
            await Promise.all([
                this.loadCurrentWorkspaceTasksBySearch(TaskPanelTabs.ACTIVE, ''),
                this.loadCurrentWorkspaceTasksBySearch(TaskPanelTabs.CLOSED, '')
            ]);
        }
    }

    public async loadCurrentWorkspaceTasksBySearch(status: TaskPanelTabs, searchInput: string): Promise<void> {
        const loadingId = uuidv4();
        this.mapWorkspaceTasksLoadingStream.next([...this.mapWorkspaceTasksLoadingStream.getValue(), loadingId]);
        const currentMapWorkspace = this.mapWorkspacesStore.currentMapWorkspaceStream.getValue();
        try {
            const tasks = await this.taskService.getTasksBySearch(
                currentMapWorkspace.projectId,
                currentMapWorkspace.id,
                status,
                searchInput
            );
            return this.updateTasks(tasks, status, loadingId);
        } catch {
            return this.updateTasks([], status, loadingId);
        }
    }

    private updateTasks(tasks: Task[], status: TaskPanelTabs, loadingId: string): void {
        let mapWorkspaceTasks: Task[];
        mapWorkspaceTasks = tasks;
        this.restoreVisibleTasks(mapWorkspaceTasks);

        if (status === TaskPanelTabs.ACTIVE) {
            this.mapWorkspaceActiveTasksStream.next(mapWorkspaceTasks);
        } else if (status === TaskPanelTabs.CLOSED) {
            this.mapWorkspaceClosedTasksStream.next(mapWorkspaceTasks);
        } else {
            this.mapWorkspaceTasksStream.next(mapWorkspaceTasks);
        }
        this.mapWorkspaceActiveTasksByTemplateSeriesIdStream.next(this.groupTasksByTemplateId(mapWorkspaceTasks));
        this.mapWorkspaceTasksLoadingStream.next(
            this.mapWorkspaceTasksLoadingStream.getValue().filter(id => id !== loadingId)
        );
        this.mapWorkspaceTasksLoadedStream.next(true);
    }

    public isTasksLoaded(): Promise<boolean> {
        const timeout = 500;
        const stop = new Subject<boolean>();

        return new Promise((resolve, reject) => {
            interval(timeout)
                .pipe(takeUntil(stop))
                .subscribe(() => {
                    if (this.mapWorkspaceTasksLoadedStream.getValue()) {
                        resolve(true);
                        stop.next(true);
                    }
                });
        });
    }

    private saveVisibleTasks(): void {
        const tasks = this.mapWorkspaceTasksStream.getValue() || [];

        this.savedVisibleTaskIds = tasks.filter(task => task.visible).map(task => task.id);
    }

    private restoreVisibleTasks(tasks: Task[]): void {
        // Check if savedVisibleTaskIds is initialized
        if (!this.savedVisibleTaskIds) {
            this.savedVisibleTaskIds = [];
            return;
        }

        if (!tasks?.length) {
            return;
        }

        tasks.forEach(task => {
            task.visible = this.savedVisibleTaskIds.indexOf(task.id) >= 0;
        });
    }

    public refreshMapWorkspaceTasks(): Promise<void> {
        const tasks = this.mapWorkspaceTasksStream.getValue();
        const visibleTasks = tasks.filter(task => task.visible);
        this.refreshMapWorkspaceTasksStreams.next(visibleTasks);
        return this.loadCurrentWorkspaceTasks(this.mapWorkspacesStore.getCurrentMapWorkspace());
    }

    public getTaskById(id: string): Promise<Task> {
        return this.taskService.getTaskById(this.mapWorkspacesStore.getCurrentMapWorkspace().projectId, id);
    }

    public createNewTask(): Promise<Task> {
        return Promise.resolve(new Task());
    }

    public async saveTask(task: Task): Promise<Task> {
        let t: Task;

        if (task.id === null) {
            t = await this.taskService.createTask(
                this.mapWorkspacesStore.getCurrentMapWorkspace().projectId,
                this.mapWorkspacesStore.getCurrentMapWorkspace(),
                task
            );
        } else {
            const taskBeforeEdit = this.mapWorkspaceTasksStream.getValue().find(t => t.id === task.id);

            t = await this.taskService.updateTask(
                this.mapWorkspacesStore.getCurrentMapWorkspace().projectId,
                task,
                this.getEditedTaskFields(taskBeforeEdit, task)
            );
        }

        return this.updateOrRemoveTaskInStore(t, false);
    }

    private getEditedTaskFields(original: Task, updated: Task): TaskPatchDTO {
        let editedProperties: TaskPatchDTO = {};

        if (original.title !== updated.title) {
            editedProperties.name = updated.title;
        }

        if (original.description !== updated.description) {
            editedProperties.description = updated.description;
        }

        if (original.status !== updated.status) {
            editedProperties.status = updated.status;
        }

        if (!_.isEqual(original.assignees, updated.assignees)) {
            const newAssignees = updated.assignees.filter(a => !original.assignees.some(oa => oa.id === a.id));
            const removeAssigneesIds = original.assignees
                .filter(oa => !updated.assignees.some(a => a.id === oa.id))
                .map(a => a.id);

            editedProperties.assignees = {
                add: [],
                removalIds: []
            };

            editedProperties.assignees.add = newAssignees;
            editedProperties.assignees.removalIds = removeAssigneesIds;
        }
        if (!_.isEqual(original.features, updated.features)) {
            const upsertForms = updated.features.filter(
                uf => !original.features.some(of => of.featureId === uf.featureId)
            );
            const removeFormsIds = original.features
                .filter(of => !updated.features.some(f => f.featureId === of.featureId))
                .map(f => f.featureId);

            editedProperties.forms = {
                upsert: upsertForms,
                removalIds: removeFormsIds
            };
        }

        return editedProperties;
    }

    public closeTask(task: Task): Promise<Task> {
        return this.taskService
            .updateTask(
                this.mapWorkspacesStore.getCurrentMapWorkspace().projectId,
                task,
                this.getEditedTaskFields(task, { ...task, status: TaskStatus.CLOSED } as Task)
            )
            .then(
                tsk => {
                    tsk.visible = false;
                    return this.updateOrRemoveTaskInStore(tsk, false);
                },
                error => {
                    this.messaging.showError(this.translate.instant('TodosTasks.SaveTaskError'));
                    return task;
                }
            );
    }

    public async reopenTask(task: Task): Promise<Task> {
        const projectId = this.mapWorkspacesStore.getCurrentMapWorkspace().projectId;
        try {
            if (task.returnedJobs.length) {
                await this.taskService.deleteTaskReturns(projectId, task.id);
            }
            const tsk = await this.taskService.updateTask(
                projectId,
                task,
                this.getEditedTaskFields(task, { ...task, status: TaskStatus.IN_PROGRESS } as Task)
            );
            tsk.visible = true;
            return this.updateOrRemoveTaskInStore(tsk, false);
        } catch {
            this.messaging.showError(this.translate.instant('TodosTasks.SaveTaskError'));
            return task;
        }
    }

    public async deleteTask(task: Task): Promise<Task> {
        await this.taskService.deleteTask(this.mapWorkspacesStore.getCurrentMapWorkspace().projectId, task);
        return this.updateOrRemoveTaskInStore(task, true);
    }

    private updateOrRemoveTaskInStore(task: Task, remove: boolean): Task {
        let mapWorkspaceTasks = this.mapWorkspaceTasksStream.getValue();
        let updateTaskIndex = _.findIndex(mapWorkspaceTasks, mapWorkspaceTask => mapWorkspaceTask.id === task.id);

        if (updateTaskIndex > -1) {
            mapWorkspaceTasks.splice(updateTaskIndex, 1);
        }
        if (!remove) {
            mapWorkspaceTasks.unshift(task);
        }
        this.mapWorkspaceTasksStream.next(mapWorkspaceTasks);
        this.mapWorkspaceActiveTasksByTemplateSeriesIdStream.next(this.groupTasksByTemplateId(mapWorkspaceTasks));

        return task;
    }

    public getAllAssignees(): Actor[] {
        const projectId = this.mapWorkspacesStore.getCurrentMapWorkspace().projectId;
        const tmpGroups = JSON.parse(sessionStorage.getItem(`project-${projectId}-groups`) || '[]') as Actor[];
        const tmpUsers = JSON.parse(sessionStorage.getItem(`project-${projectId}-users`) || '[]') as Actor[];

        return [...tmpUsers, ...tmpGroups];
    }

    private groupTasksByTemplateId(tasks: Task[]): { [templateId: string]: Task[] } {
        let activeTaskByTemplateId: { [templateId: string]: Task[] } = {};
        tasks.forEach(task => {
            let featureGroup = _.groupBy(task.features, 'templateSeriesId');
            Object.keys(featureGroup).forEach(templateId => {
                if (activeTaskByTemplateId[templateId]) {
                    activeTaskByTemplateId[templateId].push(task);
                } else {
                    activeTaskByTemplateId[templateId] = [task];
                }
            });
        });
        return activeTaskByTemplateId;
    }

    public getTaskFeaturesFromSelectedFeatures(features: Feature[]): TaskFeature[] {
        return (
            features
                // filter out features in layers which have no template
                .filter(feature => {
                    let layer: Layer = null;
                    if (feature.layerId) {
                        layer = this.layersStore.getMapWorkspaceLayerById(feature.layerId);
                    }
                    if (!layer || !layer.templateSeriesId) {
                        return false;
                    }

                    return true;
                })
                .map(
                    feature =>
                        ({
                            featureId: feature.id,
                            templateSeriesId: this.layersStore.getMapWorkspaceLayerById(feature.layerId)
                                .templateSeriesId,
                            status: 'NeedsUpdating'
                        } as TaskFeature)
                )
        );
    }

    // Tasked forms - Helper functions
    private totalFeatureNeedsUpdating(task: Task): number {
        return task.features.filter(feature => feature.status === FormStatus.NEEDS_UPDATING).length;
    }

    private totalFeatureUpdated(task: Task): number {
        return task.features.filter(feature => feature.status === FormStatus.UPDATED).length;
    }

    public totalTaskedForms(task: Task): number {
        return this.totalFeatureNeedsUpdating(task) + this.totalFeatureUpdated(task);
    }
}
